if (!customElements.get('upsell-picker')) {
  class UpsellPicker extends HTMLElement {
    connectedCallback() {
      this.variantsInformation = JSON.parse(this.querySelector('[data-upsell-picker-variants]').textContent);
      this.inputElements = [...this.querySelectorAll('[data-upsell-picker-input]')];
      this.withUpsellInputElement = this.querySelector('[data-upsell-picker-input="with"]');
      this.imageElements = [...this.querySelectorAll('[data-upsell-picker-image]')];
      this.priceElement = this.querySelector('[data-upsell-picker-price]');
      this.infoElement = this.querySelector('[data-upsell-picker-info]');
      this.infoButtonElement = this.querySelector('[data-upsell-picker-info-button]');
      this.tooltipElement = this.querySelector('[data-upsell-picker-tooltip]');
      this.artworkFormElement = this.closest('#tee-artwork-form');
      this.upsellVariantId = null;
      this.infoPointerType = null;

      this.onLocationChange = this.onLocationChange.bind(this);
      this.onAddToCartClick = this.onAddToCartClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onInfoButtonPointerDown = this.onInfoButtonPointerDown.bind(this);
      this.onInfoButtonClick = this.onInfoButtonClick.bind(this);
      this.onInfoKeydown = this.onInfoKeydown.bind(this);
      this.onInfoPointerEnter = this.onInfoPointerEnter.bind(this);
      this.onInfoPointerLeave = this.onInfoPointerLeave.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.closeTooltip = this.closeTooltip.bind(this);

      this.renderVariant(new URLSearchParams(window.location.search).get('variant') || this.dataset.initialVariantId);

      window.addEventListener('variantdetails:locationchange', this.onLocationChange);
      window.addEventListener('popstate', this.onLocationChange);
      this.artworkFormElement?.addEventListener('click', this.onAddToCartClick, true);
      this.inputElements.forEach((inputElement) => inputElement.addEventListener('change', this.onInputChange));
      this.infoButtonElement.addEventListener('pointerdown', this.onInfoButtonPointerDown);
      this.infoButtonElement.addEventListener('click', this.onInfoButtonClick);
      this.infoButtonElement.addEventListener('keydown', this.onInfoKeydown);
      this.infoButtonElement.addEventListener('blur', this.closeTooltip);
      this.infoElement.addEventListener('pointerenter', this.onInfoPointerEnter);
      this.infoElement.addEventListener('pointerleave', this.onInfoPointerLeave);
    }

    disconnectedCallback() {
      window.removeEventListener('variantdetails:locationchange', this.onLocationChange);
      window.removeEventListener('popstate', this.onLocationChange);
      this.artworkFormElement?.removeEventListener('click', this.onAddToCartClick, true);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    isActive() {
      return document.documentElement.classList.contains('upsell-picker-available') && this.upsellVariantId !== null;
    }

    onLocationChange() {
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (!variantId) return;

      this.renderVariant(variantId);
    }

    renderVariant(variantId) {
      const variantInformation = this.variantsInformation.find((information) => information.parent_id === Number(variantId));

      if (!variantInformation || variantInformation.upsell_variant_id === null) {
        this.upsellVariantId = null;
        this.classList.add('upsell-picker--empty');
        this.classList.remove('upsell-picker--invalid');
        return;
      }

      this.upsellVariantId = variantInformation.upsell_variant_id;
      this.classList.remove('upsell-picker--empty');
      this.priceElement.textContent = variantInformation.price;
      this.imageElements.forEach((imageElement) => {
        imageElement.src = variantInformation.image_url;
        imageElement.alt = variantInformation.image_alt;
      });
    }

    onAddToCartClick(event) {
      if (!event.target.closest('#teeAtcButton')) return;
      if (!this.isActive() || this.inputElements.some((inputElement) => inputElement.checked)) return;

      event.preventDefault();
      event.stopPropagation();
      this.classList.add('upsell-picker--invalid');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onInputChange() {
      this.classList.remove('upsell-picker--invalid');
    }

    onInfoButtonPointerDown(event) {
      this.infoPointerType = event.pointerType;
    }

    onInfoButtonClick() {
      const pointerType = this.infoPointerType;
      this.infoPointerType = null;

      if (pointerType === 'mouse') return;

      if (this.infoButtonElement.getAttribute('aria-expanded') === 'true') {
        this.closeTooltip();
        return;
      }

      this.openTooltip();
    }

    onInfoKeydown(event) {
      if (event.key !== 'Escape') return;

      this.closeTooltip();
    }

    onInfoPointerEnter(event) {
      if (event.pointerType !== 'mouse') return;

      this.openTooltip();
    }

    onInfoPointerLeave(event) {
      if (event.pointerType !== 'mouse') return;

      this.closeTooltip();
    }

    onDocumentPointerDown(event) {
      if (this.infoElement.contains(event.target)) return;

      this.closeTooltip();
    }

    openTooltip() {
      this.infoButtonElement.setAttribute('aria-expanded', 'true');
      this.infoElement.classList.add('upsell-picker__info--open');
      document.addEventListener('pointerdown', this.onDocumentPointerDown);

      const pickerRectangle = this.getBoundingClientRect();
      this.tooltipElement.style.setProperty('--upsell-picker-tooltip-max-width', `${pickerRectangle.width - 8}px`);
      this.tooltipElement.style.setProperty('--upsell-picker-tooltip-offset', '0px');

      const tooltipRectangle = this.tooltipElement.getBoundingClientRect();
      const overflowLeft = pickerRectangle.left + 4 - tooltipRectangle.left;
      const overflowRight = tooltipRectangle.right - (pickerRectangle.right - 4);
      let offset = 0;

      if (overflowLeft > 0) offset = overflowLeft;
      else if (overflowRight > 0) offset = -overflowRight;

      this.tooltipElement.style.setProperty('--upsell-picker-tooltip-offset', `${offset}px`);
    }

    closeTooltip() {
      this.infoButtonElement.setAttribute('aria-expanded', 'false');
      this.infoElement.classList.remove('upsell-picker__info--open');
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    }

    addToCart(parentItem) {
      if (!this.isActive() || !this.withUpsellInputElement.checked) return Promise.resolve(0);

      const formData = new FormData();
      formData.append('id', this.upsellVariantId);
      formData.append('quantity', 1);
      formData.append('properties[_is_bearfamily_upsell]', 'true');
      formData.append('properties[_is_upsell]', 'true');
      Object.entries(parentItem.properties || {}).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
      formData.append('parent_line_key', parentItem.key);

      return fetch('/cart/add.js', { method: 'POST', body: formData })
        .then((response) => (response.ok ? 1 : 0))
        .catch(() => 0);
    }
  }

  customElements.define('upsell-picker', UpsellPicker);
}
